/**
 * Bilder werden im Browser verkleinert, bevor sie zum Server gehen.
 * Ein Handyfoto hat gern 4-8 MB -- als Hintergrund reichen 1600 px.
 */

export interface ImagePreset {
  maxWidth: number;
  maxHeight: number;
  mime: 'image/jpeg' | 'image/png';
  quality: number;
}

export const COVER_PRESET: ImagePreset = { maxWidth: 1600, maxHeight: 900, mime: 'image/jpeg', quality: 0.82 };
/** PNG, damit freigestellte Vereinslogos ihre Transparenz behalten. */
export const LOGO_PRESET: ImagePreset = { maxWidth: 512, maxHeight: 512, mime: 'image/png', quality: 1 };
/**
 * Werbematerial: Logo, Bannerbild, Eckbild oder L-Grafik in 1920 × 1080 --
 * also bis Vollbildgroesse. PNG bleibt PNG (Transparenz, scharfe Schrift),
 * ein Foto als JPEG bleibt JPEG: als PNG haette eine L-Grafik in voller
 * Groesse leicht mehr als die 4 MB, die der Server annimmt.
 */
export const materialImagePreset = (file: File): ImagePreset =>
  file.type === 'image/jpeg'
    ? { maxWidth: 1920, maxHeight: 1080, mime: 'image/jpeg', quality: 0.9 }
    : { maxWidth: 1920, maxHeight: 1080, mime: 'image/png', quality: 1 };
/**
 * Buehnenhintergrund. Etwas groesser als das Titelbild, weil er formatfuellend
 * hinter dem Kamerabild liegt und im Vollbild sonst weich wird -- aber nicht
 * groesser als 1920, sonst kostet er den Renderer beim Weichzeichnen Zeit.
 */
export const BACKDROP_PRESET: ImagePreset = { maxWidth: 1920, maxHeight: 1080, mime: 'image/jpeg', quality: 0.84 };

/**
 * Headshot fuers Athleten-Pop-up. Im Bild steht es rund und klein -- 600 px
 * reichen auch im Vollbild auf dem Beamer, und ein Handyfoto wird so ein
 * paar Dutzend KB statt mehrerer MB.
 */
export const HEADSHOT_PRESET: ImagePreset = { maxWidth: 600, maxHeight: 600, mime: 'image/jpeg', quality: 0.86 };

const MAX_INPUT_BYTES = 20 * 1024 * 1024;

export async function fileToDataUrl(file: File, preset: ImagePreset): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('Bitte eine Bilddatei wählen.');
  if (file.size > MAX_INPUT_BYTES) throw new Error('Bild ist größer als 20 MB.');

  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error('Bild konnte nicht gelesen werden.');
  });

  const scale = Math.min(1, preset.maxWidth / bitmap.width, preset.maxHeight / bitmap.height);
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas nicht verfügbar.');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const dataUrl = canvas.toDataURL(preset.mime, preset.quality);
  // Grober Schutz: der Server nimmt maximal 4 MB entgegen.
  if (dataUrl.length > 5.4 * 1024 * 1024) throw new Error('Bild bleibt auch verkleinert zu groß.');
  return dataUrl;
}
